import Box from '@mui/material/Box';
import React from 'react';

import { useBrandingIdentity } from '../../../context/BrandingContext';
import { BrandIcon } from '../../../icons';

export interface BrandedLogoProps {
  /** Height of the logo in pixels */
  height?: number;
  /** Width of the logo in pixels */
  width?: number;
  /** Additional CSS class name */
  className?: string;
  /** Whether to show the practice name next to the logo if no custom logo */
  showName?: boolean;
  /** Alt text for the logo image */
  alt?: string;
}

/**
 * BrandedLogo component displays the practice logo if configured,
 * otherwise falls back to the default HealioSpace brand icon.
 *
 * @example
 * ```tsx
 * // In header
 * <BrandedLogo height={30} width={120} />
 *
 * // With practice name fallback
 * <BrandedLogo height={40} showName />
 * ```
 */
export const BrandedLogo: React.FC<BrandedLogoProps> = ({
  height = 30,
  width = 120,
  className,
  showName = false,
  alt,
}) => {
  const { displayName, logoUrl, hasCustomBranding } = useBrandingIdentity();

  // If practice has a custom logo, display it
  if (logoUrl) {
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={alt || displayName}
        className={className}
        sx={{
          height,
          width: 'auto',
          maxWidth: width,
          objectFit: 'contain',
        }}
      />
    );
  }

  // If practice has custom branding but no logo, show practice name
  if (hasCustomBranding && showName && displayName !== 'HealioSpace') {
    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height,
          fontWeight: 700,
          fontSize: height * 0.6,
          color: 'var(--branding-primary-color, #4A90D9)',
          letterSpacing: '-0.5px',
        }}
      >
        {displayName}
      </Box>
    );
  }

  // Default: show HealioSpace brand icon
  return <BrandIcon className={className} />;
};

export default BrandedLogo;
